/**
 * Critical Safety Utility for Default_PC Prevention
 *
 * This module provides functionality to prevent "Default_PC" from being published
 * via R2F or GSLA operations, as specified by the user requirement:
 * "One important thing to prevent is we should never allow r2f or gsla to publish
 * for Default_PC that specific entry would cause lot's of issue"
 */

export interface DefaultPCValidationResult {
    hasDefaultPC: boolean;
    filteredItems: any[];
    defaultPCCount: number;
    warningMessage?: string;
}

export interface DefaultPCSafetyBlockResult {
    shouldBlock: boolean;
    errorMessage: string;
    userMessage: string;
    defaultPCCount: number;
}

/**
 * The exact string that must be prevented from being published
 */
export const FORBIDDEN_PC_NAME = 'Default_PC';

/**
 * Validates and filters out any entries with "Default_PC" as production capability
 *
 * @param items - Array of items to validate (R2F changes, GSLA changes, etc.)
 * @param getProductionCapability - Function to extract PC name from each item
 * @returns Validation result with filtered items and warning information
 */
export function validateAndFilterDefaultPC<T>(
    items: T[],
    getProductionCapability: (item: T) => string
): DefaultPCValidationResult {
    if (!items || items.length === 0) {
        return {
            hasDefaultPC: false,
            filteredItems: [],
            defaultPCCount: 0
        };
    }

    const defaultPCItems: T[] = [];
    const safeItems: T[] = [];

    // Separate Default_PC items from safe items
    items.forEach(item => {
        const pcName = getProductionCapability(item);
        if (pcName === FORBIDDEN_PC_NAME) {
            defaultPCItems.push(item);
        } else {
            safeItems.push(item);
        }
    });

    const hasDefaultPC = defaultPCItems.length > 0;
    const warningMessage = hasDefaultPC
        ? `CRITICAL SAFETY WARNING: ${defaultPCItems.length} "Default_PC" entries were excluded from this operation to prevent system issues. Please review your changes and remove any "Default_PC" entries before saving.`
        : undefined;

    return {
        hasDefaultPC,
        filteredItems: safeItems,
        defaultPCCount: defaultPCItems.length,
        warningMessage
    };
}

/**
 * Consolidated validation function for both R2F and GSLA operations
 * Returns safety block information with consistent messaging
 */
export function validateForDefaultPCSafety(
    changes: any[],
    operationType: 'R2F' | 'GSLA',
    context: string = ''
): DefaultPCSafetyBlockResult {
    const validation = validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
    
    if (validation.hasDefaultPC) {
        const errorMessage = createDefaultPCBlockedMessage(operationType, validation.defaultPCCount);
        const userMessage = `🛡️ SAVE BLOCKED: ${validation.defaultPCCount} "${FORBIDDEN_PC_NAME}" entries were blocked for safety. Please remove all "${FORBIDDEN_PC_NAME}" entries and try again.`;
        
        return {
            shouldBlock: true,
            errorMessage,
            userMessage,
            defaultPCCount: validation.defaultPCCount
        };
    }
    
    return {
        shouldBlock: false,
        errorMessage: '',
        userMessage: '',
        defaultPCCount: 0
    };
}

/**
 * Validates R2F threshold changes for Default_PC
 */
export function validateR2FChangesForDefaultPC(changes: any[]): DefaultPCValidationResult {
    return validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
}

/**
 * Validates GSLA changes for Default_PC
 */
export function validateGSLAChangesForDefaultPC(changes: any[]): DefaultPCValidationResult {
    return validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
}

/**
 * Validates R2F bulk publish requests for Default_PC
 */
export function validateR2FBulkRequestForDefaultPC(publishRequests: any[]): DefaultPCValidationResult {
    const allChanges: any[] = [];
    
    // Extract all production capabilities from the bulk request
    publishRequests.forEach(request => {
        if (request.r2fToPublish) {
            Object.keys(request.r2fToPublish).forEach(pcName => {
                allChanges.push({ productionCapability: pcName, warehouseId: request.warehouseId });
            });
        }
    });

    const validation = validateAndFilterDefaultPC(allChanges, (change) => change.productionCapability || '');
    
    if (validation.hasDefaultPC) {
        // Filter the original publish requests to remove Default_PC entries
        const filteredRequests = publishRequests.map(request => {
            if (!request.r2fToPublish) return request;
            
            const filteredR2FToPublish = { ...request.r2fToPublish };
            delete filteredR2FToPublish[FORBIDDEN_PC_NAME];
            
            return {
                ...request,
                r2fToPublish: filteredR2FToPublish
            };
        }).filter(request =>
            // Remove requests that have no production capabilities left after filtering
            request.r2fToPublish && Object.keys(request.r2fToPublish).length > 0
        );

        return {
            ...validation,
            filteredItems: filteredRequests
        };
    }

    return {
        ...validation,
        filteredItems: publishRequests
    };
}

/**
 * Creates a user-friendly error message when Default_PC is detected
 */
export function createDefaultPCBlockedMessage(operationType: 'R2F' | 'GSLA', count: number): string {
    return `❌ SAVE BLOCKED FOR SAFETY: Found ${count} "${FORBIDDEN_PC_NAME}" entr${count === 1 ? 'y' : 'ies'} in your ${operationType} changes.

"${FORBIDDEN_PC_NAME}" cannot be published as it would cause serious system issues.

Please remove all "${FORBIDDEN_PC_NAME}" entries from your changes and try saving again.`;
}

/**
 * Logs safety violations for audit purposes
 */
export function logDefaultPCSafetyViolation(
    operationType: 'R2F' | 'GSLA',
    userAlias: string,
    defaultPCCount: number,
    warehouseId?: string
): void {
    const logMessage = `DEFAULT_PC_SAFETY_VIOLATION: User ${userAlias} attempted to publish ${defaultPCCount} "${FORBIDDEN_PC_NAME}" entries via ${operationType}${warehouseId ? ` for warehouse ${warehouseId}` : ''}. Operation was blocked for safety.`;
    
    console.warn(logMessage);
    
    // In a production environment, this would also send to monitoring/alerting systems
    // Example: sendToMonitoring('SECURITY_VIOLATION', logMessage);
}